import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { CheckCircle2, Clock, AlertCircle, Calendar, ExternalLink, CheckCircle, Circle, Mail, ArrowRight } from "lucide-react"
import { mockPlaybookRun, mockTaskGroups } from "@/lib/mock/customer-success"
import { PlaybookRun, TaskGroup, PlaybookTask } from "@/types/customer-success"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import Breadcrumb from "@/components/common/Breadcrumb"
import { StatusBadge } from '@/components/common/StatusBadge'

// Estendendo os tipos para incluir as propriedades necessárias
interface ExtendedPlaybookRun extends PlaybookRun {
  target_end?: string;
  company_name?: string;
}

interface ExtendedPlaybookTask extends PlaybookTask {
  assigned_to?: string;
  due_date?: string;
}

export default function PlaybookPublicView() {
  const { id, token } = useParams<{ id: string; token: string }>()
  const [playbookRun, setPlaybookRun] = useState<ExtendedPlaybookRun | null>(null)
  const [taskGroups, setTaskGroups] = useState<TaskGroup[]>([])
  const [loading, setLoading] = useState(true)
  
  // In a real implementation, this would fetch data from an API using the token for authentication
  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      // Adicionar propriedades extras ao mockPlaybookRun
      const extendedPlaybookRun: ExtendedPlaybookRun = {
        ...mockPlaybookRun,
        target_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 dias a partir de agora
        company_name: "Acme Corporation"
      };
      
      // Adicionar propriedades extras às tarefas
      const extendedTaskGroups = mockTaskGroups.map(group => ({
        ...group,
        tasks: group.tasks.map(task => ({
          ...task,
          assigned_to: task.status === 'completed' ? 'João Silva' : 'Maria Oliveira',
          due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 dias a partir de agora
        })) as ExtendedPlaybookTask[]
      }));
      
      setPlaybookRun(extendedPlaybookRun)
      setTaskGroups(extendedTaskGroups)
      setLoading(false)
    }, 1000)
    
    return () => clearTimeout(timer)
  }, [id, token])
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando playbook...</p>
        </div>
      </div>
    )
  }
  
  if (!playbookRun) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-extrabold">Playbook não encontrado</p>
          <p className="mt-2 text-muted-foreground">O playbook solicitado não existe ou o link expirou.</p>
        </div>
      </div>
    )
  }
  
  const completedTasks = playbookRun.tasks?.filter(task => task.status === 'completed').length || 0
  const totalTasks = playbookRun.tasks?.length || 0
  const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'current':
        return <Clock className="h-5 w-5 text-blue-500" />
      case 'pending':
        return <Circle className="h-5 w-5 text-gray-300" />
      case 'overdue':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      default:
        return <Circle className="h-5 w-5 text-gray-300" />
    }
  }
  
  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Partnership Header */}
      <div className="bg-[#1F1746] py-6 border-b border-[#2a3e70] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col">
            <div className="flex mb-4">
              <div className="flex -space-x-4">
                <div className="relative z-10">
                  <Avatar className="h-16 w-16 border-2 border-white bg-white">
                    <AvatarImage src="https://ui-avatars.com/api/?name=T&background=0D9488&color=fff" alt="Trivium" />
                    <AvatarFallback className="bg-primary text-white text-xl">T</AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 bg-primary text-white text-xs font-extrabold h-6 w-6 rounded-full flex items-center justify-center z-20">
                    T
                  </div>
                </div>
                
                <div className="relative">
                  <Avatar className="h-16 w-16 border-2 border-white bg-white">
                    <AvatarImage 
                      src={`https://ui-avatars.com/api/?name=${playbookRun?.client_name?.charAt(0) || 'C'}&background=F0F0F0&color=333`} 
                      alt={playbookRun?.client_name || 'Cliente'} 
                    />
                    <AvatarFallback className="bg-gray-100 text-gray-800 text-xl">
                      {playbookRun?.client_name?.charAt(0) || 'C'}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </div>
            </div>
            
            <h1 className="text-2xl font-extrabold text-white">
              Trivium <span className="mx-2">↔</span> {playbookRun?.client_name || 'Cliente'}
            </h1>
            <p className="text-lg font-extrabold text-gray-200 mt-1">
              {playbookRun?.template?.name || 'Proposta de Parceria'}
            </p>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        <main className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          {/* Merged Overview and CS Manager Card */}
          <Card className="mb-8 overflow-hidden border border-gray-200">
            <div className="bg-[#1F1746] h-8"></div>
            <CardContent className="pt-6 pb-6">
              <div className="flex flex-col md:flex-row gap-8">
                {/* CS Manager Section */}
                <div className="flex flex-col sm:flex-row md:flex-col items-center gap-4 md:border-r md:pr-6 md:min-w-[220px] pb-4 md:pb-0 border-b md:border-b-0 border-gray-100">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white -mt-12 shadow-sm">
                      <img 
                        src="https://randomuser.me/api/portraits/women/44.jpg" 
                        alt="Angela Jones" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="text-center md:text-center">
                    <h2 className="text-lg font-extrabold">Hi! I'm Angela Jones</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      I'm your implementation manager
                    </p>
                    <div className="flex items-center gap-2 mt-2 justify-center">
                      <Mail className="h-4 w-4 text-blue-600" />
                      <a href="mailto:angela@a-bordeaux.com" className="text-sm text-blue-600 hover:underline">
                        angela@a-bordeaux.com
                      </a>
                    </div>
                  </div>
                </div>
                
                {/* Overview Section */}
                <div className="flex-1 pt-2 md:pt-0">
                  <h3 className="text-lg font-extrabold mb-4 text-gray-800">Visão Geral do Onboarding</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-extrabold text-muted-foreground mb-1">Taxa de conclusão</p>
                      <div className="flex items-center gap-3">
                        <div className="text-2xl font-extrabold">{progress}%</div>
                        <div className="flex-1">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-primary h-2.5 rounded-full" 
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm font-extrabold text-muted-foreground mb-1">Datas do projeto</p>
                      <div className="text-2xl font-extrabold" style={{ fontSize: "90%" }}>
                        {new Date(playbookRun.actual_start || playbookRun.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short'
                        }).replace('.', '')} - {playbookRun.target_end ? new Date(playbookRun.target_end).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short'
                        }).replace('.', '') : 'N/A'}
                      </div>
                    </div>
                    
                    {/* Status Section */}
                    <div className="sm:col-span-2 mt-2">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={playbookRun.status} context="job-execution" />
                        <span className="text-sm text-muted-foreground">
                          {playbookRun.status === 'running' ? 'Seu onboarding está em andamento' : 
                           playbookRun.status === 'completed' ? 'Seu onboarding foi concluído' : 
                           'Aguardando início do onboarding'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Next Task - Only if there's a current task */}
                    {playbookRun.tasks?.some(task => task.status === 'current') && (
                      <div className="sm:col-span-2">
                        <p className="text-sm font-extrabold text-muted-foreground mb-1">Próxima tarefa</p>
                        <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                          <div className="flex items-start gap-2">
                            <Clock className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <div className="text-sm font-extrabold text-blue-700">
                              {playbookRun.tasks.find(task => task.status === 'current')?.title}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Task Groups */}
          <div className="space-y-6">
            {taskGroups.map((group) => (
              <Card key={group.id} id={`group-${group.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{group.icon}</span>
                    <CardTitle>{group.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {group.tasks.map((task, index) => {
                      // Cast para o tipo estendido
                      const extendedTask = task as ExtendedPlaybookTask;
                      
                      return (
                        <div key={task.id}>
                          {index > 0 && <Separator className="my-4" />}
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              {getStatusIcon(task.status)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-extrabold text-base">{task.title}</h4>
                                  {task.description && (
                                    <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                                  )}
                                </div>
                                <div>
                                  <StatusBadge status={task.status} context="job-execution" />
                                </div>
                              </div>
                              
                              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="font-extrabold">Responsável</p>
                                  <p className="text-muted-foreground">
                                    {extendedTask.assigned_to || 'Não atribuído'}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-extrabold">Prazo</p>
                                  <p className="text-muted-foreground">
                                    {extendedTask.due_date ? new Date(extendedTask.due_date).toLocaleDateString() : 'Sem prazo definido'}
                                  </p>
                                </div>
                              </div>
                              
                              {task.status === 'current' && (
                                <div className="mt-4">
                                  <Button size="sm">
                                    Visualizar detalhes
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Contact Section */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Precisa de ajuda?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="text-muted-foreground">
                    Se você tiver dúvidas ou precisar de assistência, entre em contato com seu gerente de sucesso do cliente.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Agendar reunião
                  </Button>
                  <Button>
                    Enviar mensagem
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  )
} 